import { type RefObject, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { isMobileQuery } from '@cx-modules/react-utils';
import { detectBrowser } from '@cx-modules/utils';

export interface UseIntersectionOptions {
  element?: Element | null;
  handleIntersection(isIntersecting: boolean): void;
  setRootMargin?(): string;
  threshold?: number;
}

export function useIntersection<E extends HTMLElement>({
  element,
  handleIntersection,
  setRootMargin,
  threshold,
}: UseIntersectionOptions): RefObject<E | null> {
  const isMobile = useMediaQuery(isMobileQuery);

  const ref = useRef<E | null>(null);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    if (detectBrowser() === 'safari' && !isMobile) {
      setIsSafari(true);
    }
  }, [isMobile]);

  useEffect(() => {
    const currentRef = ref.current;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(({ isIntersecting }) => {
          handleIntersection(isIntersecting);
        });
      },
      {
        root: isSafari ? document.body : null,
        ...(setRootMargin ? { rootMargin: setRootMargin() } : {}),
        ...(threshold ? { threshold } : {}),
      },
    );

    if (element) observer.observe(element);
    else if (ref.current) observer.observe(ref.current);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [element, handleIntersection, isSafari, setRootMargin, threshold]);

  return ref;
}
